import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Input, InputNumber, Space, Table, Typography } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { parsePastedSeries, SeriesKind } from './paste';
import { Panel, S } from './ui';

const { Text } = Typography;

interface Props {
  title: string;
  help: React.ReactNode;
  placeholder: string;
  kind: SeriesKind;
  /** Column headings for the value columns, in order. */
  valueLabels: string[];
  periods: string[];
  columns: number[][];
  onChange: (periods: string[], columns: number[][]) => void;
  disabled?: boolean;
}

/**
 * The series editor: an editable grid, with a paste box behind it for bulk replacement.
 *
 * The grid is the default view because correcting one figure is now the common case --
 * whole months arrive through the pre-fill card, so pasting a block by hand is the
 * exception rather than the routine.
 *
 * The paste box keeps its explicit button rather than applying as you type. A paste
 * REPLACES the entire series, so parsing on every keystroke would cut 23 rows down to one
 * while someone was halfway through typing the first. The button is the commit point for a
 * destructive operation; the grid handles everything else.
 */
const SeriesPasteEditor: React.FC<Props> = ({
  title, help, placeholder, kind, valueLabels, periods, columns, onChange, disabled,
}) => {
  const [draftText, setDraftText] = useState('');
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [lastResult, setLastResult] = useState<{ added: number; skipped: number } | null>(null);
  const [pasteOpen, setPasteOpen] = useState(false);

  // Keep the box open if a paste failed, so the message sits beside what it is about.
  useEffect(() => {
    if (parseErrors.length) setPasteOpen(true);
  }, [parseErrors]);

  const rows = useMemo(
    () => periods.map((p, i) => ({
      key: p,
      period: p,
      index: i,
      ...Object.fromEntries(valueLabels.map((l, c) => [`v${c}`, columns[c]?.[i]])),
    })),
    [periods, columns, valueLabels],
  );

  /** Replaces one cell, leaving the rest of the series exactly as it was. */
  const setCell = (rowIndex: number, col: number, value: number | null) => {
    const next = columns.map((c) => [...c]);
    next[col][rowIndex] = value as number;
    onChange(periods, next);
  };

  const applyPaste = () => {
    const result = parsePastedSeries(draftText, kind, valueLabels.length);
    setParseErrors(result.errors);
    if (result.errors.length || !result.rows.length) {
      setLastResult(null);
      return;
    }
    onChange(
      result.rows.map((r) => r.period),
      valueLabels.map((_, c) => result.rows.map((r) => r.values[c])),
    );
    setLastResult({ added: result.rows.length, skipped: result.skipped });
    setDraftText('');
  };

  const tableColumns = [
    {
      title: 'Period',
      dataIndex: 'period',
      key: 'period',
      width: 110,
      render: (v: string) => <Text type="secondary">{v}</Text>,
    },
    ...valueLabels.map((label, c) => ({
      title: label,
      dataIndex: `v${c}`,
      key: `v${c}`,
      width: 130,
      render: (value: number, row: any) => (
        <InputNumber
          size="small"
          style={{ width: '100%' }}
          value={value}
          disabled={disabled}
          step={0.1}
          status={value === undefined || value === null ? 'error' : undefined}
          onChange={(v) => setCell(row.index, c, v)}
        />
      ),
    })),
  ];

  const latest = periods.length
    ? `${periods[periods.length - 1]}  ${valueLabels
        .map((l, c) => `${l} ${columns[c]?.[periods.length - 1] ?? '?'}`)
        .join(', ')}`
    : null;

  return (
    <Panel
      title={title}
      aside={
        <Text type="secondary" style={{ fontSize: 12 }}>
          {periods.length
            ? `${periods.length} periods, ${periods[0]} to ${periods[periods.length - 1]}`
            : 'nothing entered yet'}
        </Text>
      }
    >
      <Text
        type="secondary"
        style={{ fontSize: 13, lineHeight: 1.6, display: 'block', maxWidth: 700 }}
      >
        {help} Edit any value directly in the table.
      </Text>

      {latest && (
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: S.xs }}>
          latest: {latest}
        </Text>
      )}

      <Table
        size="small"
        rowKey="key"
        dataSource={rows}
        columns={tableColumns}
        pagination={rows.length > 14 ? { pageSize: 14, size: 'small' } : false}
        style={{ marginTop: S.sm, maxWidth: 560 }}
      />

      {/*
        Collapsed by default. Whole months arrive through the pre-fill card now, so
        replacing a series by hand is the rare path.
      */}
      <Button
        type="link"
        size="small"
        style={{ paddingLeft: 0, marginTop: S.sm }}
        onClick={() => setPasteOpen((v) => !v)}
      >
        {pasteOpen ? <DownOutlined /> : <RightOutlined />} Replace the whole series from a paste
      </Button>

      {pasteOpen && (
        <div style={{ marginTop: S.sm }}>
          <Input.TextArea
            style={{
              display: 'block',
              maxWidth: 520,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 12,
            }}
            rows={4}
            value={draftText}
            disabled={disabled}
            onChange={(e) => setDraftText(e.target.value)}
            placeholder={placeholder}
          />
          <Space style={{ marginTop: S.sm }} wrap>
            <Button
              type="primary"
              ghost
              onClick={applyPaste}
              disabled={disabled || !draftText.trim()}
            >
              Replace all {periods.length} periods
            </Button>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Discards the whole series and rebuilds it from what you pasted.
            </Text>
          </Space>

          {parseErrors.length > 0 && (
            <Alert
              style={{ marginTop: 12, maxWidth: 700 }}
              type="error"
              showIcon
              message="That paste could not be read"
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {parseErrors.map((e) => <li key={e}>{e}</li>)}
                </ul>
              }
            />
          )}

          {lastResult && (
            <Alert
              style={{ marginTop: 12, maxWidth: 700 }}
              type="success"
              showIcon
              message={
                `Replaced with ${lastResult.added} periods` +
                (lastResult.skipped
                  ? `. Ignored ${lastResult.skipped} row${lastResult.skipped === 1 ? '' : 's'} with ` +
                    'no value: column headings, or a period the sheet has opened but not filled yet.'
                  : '.')
              }
            />
          )}
        </div>
      )}
    </Panel>
  );
};

export default SeriesPasteEditor;
